<template>
  <div class="stats-page">
    <!-- 实时数据部分 -->
    <div class="stats-section">
      <div class="section-header">
        <h2>实时数据</h2>
        <p class="current-date">{{ currentDate }}</p>
      </div>
      
      <div class="realtime-stats">
        <div class="stat-card">
          <div class="stat-title">当前注册用户数</div>
          <div class="stat-value primary">{{ loading ? '正在加载...' : userCount }}</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-title">今日新增</div>
          <div class="stat-value">{{ newUsersStats.today?.current || 0 }}</div>
          <div class="stat-compare">昨日：{{ newUsersStats.today?.previous || 0 }}</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-title">本周新增</div>
          <div class="stat-value">{{ newUsersStats.week?.current || 0 }}</div>
          <div class="stat-compare">上周：{{ newUsersStats.week?.previous || 0 }}</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-title">本月新增</div>
          <div class="stat-value">{{ newUsersStats.month?.current || 0 }}</div>
          <div class="stat-compare">上月：{{ newUsersStats.month?.previous || 0 }}</div>
        </div>
      </div>
    </div>
    
    <!-- 统计数据部分 -->
    <div class="stats-section">
      <div class="section-header">
        <h2>统计数据</h2>
        <div class="header-actions">
          <button class="refresh-btn" @click="refreshActivityData" :disabled="refreshing">
            {{ refreshing ? '刷新中...' : '刷新数据' }}
          </button>
          <p class="update-time">更新时间：{{ updateTime }}</p>
        </div>
      </div>
      
      <div v-if="refreshError" class="refresh-error">{{ refreshError }}</div>
      <div v-if="loading">正在加载...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="activityData">
        <!-- 用户增长统计 -->
        <div class="stat-group">
          <h3>用户增长统计</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>累计用户数趋势</h4>
              <v-chart class="chart" :option="totalUsersChartOption" :key="`total-users-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>周新增用户统计</h4>
              <v-chart class="chart" :option="weeklyNewUsersChartOption" :key="`weekly-new-users-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>月新增用户统计</h4>
              <v-chart class="chart" :option="monthlyNewUsersChartOption" :key="`monthly-new-users-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- 用户活跃度统计 -->
        <div class="stat-group">
          <h3>用户登录活跃度统计</h3>
          <div class="charts-container">
            <div class="chart-section">
              <h4>日活跃用户趋势</h4>
              <v-chart class="chart" :option="dailyChartOption" :key="`daily-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>周活跃用户趋势</h4>
              <v-chart class="chart" :option="weeklyChartOption" :key="`weekly-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>月活跃用户趋势</h4>
              <v-chart class="chart" :option="monthlyChartOption" :key="`monthly-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- 用户留存统计 -->
        <div class="stat-group">
          <h3>用户登录留存统计</h3>
          <div class="retention-charts">
            <div class="chart-section">
              <h4>月度留存率热力图</h4>
              <v-chart class="chart" :option="retentionChartOption" :key="`retention-heatmap-${chartUpdateKey}`"></v-chart>
            </div>
            <div class="chart-section">
              <h4>月度留存率趋势图</h4>
              <v-chart class="chart" :option="retentionLineOption" :key="`retention-line-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
        
        <!-- 用户登录统计 -->
        <div class="stat-group">
          <h3>用户登录天数统计</h3>
          <div class="login-stats-summary" v-if="loginDaysData">
            <div class="login-stat-item">
              <span class="login-stat-label">平均登录天数:</span>
              <span class="login-stat-value">{{ loginDaysData.averageLoginDays?.toFixed(2) || '-' }}天</span>
            </div>
            <div class="login-stat-item">
              <span class="login-stat-label">中位数:</span>
              <span class="login-stat-value">{{ loginDaysData.medianLoginDays || '-' }}天</span>
            </div>
            <div class="login-stat-item">
              <span class="login-stat-label">最高:</span>
              <span class="login-stat-value">{{ loginDaysData.maxLoginDays || '-' }}天</span>
            </div>
          </div>
          <div class="login-days-charts">
            <div class="chart-section">
              <h4>登录天数分布</h4>
              <v-chart class="chart" :option="loginDaysDistributionOption" :key="`login-days-${chartUpdateKey}`"></v-chart>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VChart from 'vue-echarts'

export default {
  name: 'HomePage',
  components: {
    VChart
  },
  data() {
    return {
      // 数据加载状态
      loading: true,
      refreshing: false,
      error: null,
      refreshError: null,
      
      // 统计数据
      userCount: {},
      activityData: {},
      loginDaysData: {},
      newUsersStats: {},
      updateTime: '',
      
      // 图表更新相关
      currentDate: '',
      chartUpdateKey: 0,
      
      // 为 ResizeObserver 添加节流
      resizeObserverThrottleTimer: null
    }
  },
  computed: {
    // 这里将复制App.vue中的所有computed属性
    chartBaseOption() {
      return {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          top: 40,
          right: 20,
          bottom: 60,
          left: 60,
          containLabel: true
        },
        dataZoom: [
          {
            type: 'slider',
            show: true,
            start: 0,
            end: 100,
            bottom: 10
          },
          {
            type: 'inside',
            start: 0,
            end: 100
          }
        ],
        xAxis: {
          type: 'category',
          axisLabel: {
            rotate: 45,
            interval: 'auto'
          }
        },
        yAxis: {
          type: 'value'
        },
        noDataLoadingOption: {
          text: '暂无数据',
          textStyle: {
            fontSize: 14,
            color: '#909399'
          },
          effect: 'bubble'
        }
      }
    },
    
    totalUsersChartOption() {
      // 增强检查，确保数据结构完整
      if (!this.activityData?.userCount?.dates) return {};
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(this.activityData.userCount.dates);
      
      const dates = this.activityData.userCount.dates.slice(startIndex);
      const totalUsers = this.activityData.userCount.totalUsers.slice(startIndex);
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        series: [
          {
            name: '累计用户数',
            type: 'line',
            data: totalUsers,
            areaStyle: {
              opacity: 0.2
            },
            itemStyle: {
              color: '#409EFF'
            }
          }
        ]
      };
    },
    
    weeklyNewUsersChartOption() {
      if (!this.activityData?.weeklyNewUsers?.dates) return {};
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(this.activityData.weeklyNewUsers.dates);
      
      const dates = this.activityData.weeklyNewUsers.dates.slice(startIndex);
      const newUsers = this.activityData.weeklyNewUsers.newUsers.slice(startIndex);
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        series: [
          {
            name: '新增用户数',
            type: 'bar',
            data: newUsers,
            barMaxWidth: 30,
            itemStyle: {
              color: '#67C23A'
            }
          }
        ]
      };
    },
    
    dailyChartOption() {
      if (!this.activityData?.daily?.dates) return {};
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(this.activityData.daily.dates);
      
      const dates = this.activityData.daily.dates.slice(startIndex);
      const activeUsers = this.activityData.daily.activeUsers.slice(startIndex);
      const active3Days = this.activityData.daily.active3Days.slice(startIndex);
      const active10Days = this.activityData.daily.active10Days.slice(startIndex);
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates
        },
        legend: {
          data: ['登录1天', '登录3天', '登录10天']
        },
        series: [
          {
            name: '登录1天',
            type: 'line',
            data: activeUsers
          },
          {
            name: '登录3天',
            type: 'line',
            data: active3Days
          },
          {
            name: '登录10天',
            type: 'line',
            data: active10Days
          }
        ]
      };
    },
    weeklyChartOption() {
      if (!this.activityData?.weekly?.dates) return {};
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(this.activityData.weekly.dates);
      
      const dates = this.activityData.weekly.dates.slice(startIndex);
      const activeUsers = this.activityData.weekly.activeUsers.slice(startIndex);
      const active3Days = this.activityData.weekly.active3Days.slice(startIndex);
      const active10Days = this.activityData.weekly.active10Days.slice(startIndex);
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates,
          axisLabel: {
            ...this.chartBaseOption.xAxis.axisLabel,
            formatter: function(value) {
              if (!value) return '';
              const [year, month, day] = value.split('-').map(Number);
              if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
              const weekNum = Math.ceil((new Date(year, month - 1, day).getTime() - new Date(year, 0, 1).getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;
              return `${year}年第${weekNum}周`;
            }
          }
        },
        legend: {
          data: ['登录1天', '登录3天', '登录10天']
        },
        series: [
          {
            name: '登录1天',
            type: 'line',
            data: activeUsers
          },
          {
            name: '登录3天',
            type: 'line',
            data: active3Days
          },
          {
            name: '登录10天',
            type: 'line',
            data: active10Days
          }
        ]
      }
    },
    monthlyChartOption() {
      if (!this.activityData?.monthly?.dates) return {};
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(this.activityData.monthly.dates);
      
      const dates = this.activityData.monthly.dates.slice(startIndex);
      const activeUsers = this.activityData.monthly.activeUsers.slice(startIndex);
      const active3Days = this.activityData.monthly.active3Days.slice(startIndex);
      const active10Days = this.activityData.monthly.active10Days.slice(startIndex);
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: dates,
          axisLabel: {
            ...this.chartBaseOption.xAxis.axisLabel,
            formatter: function(value) {
              if (!value) return '';
              const [year, month] = value.split('-').map(Number);
              if (isNaN(year) || isNaN(month)) return '';
              return `${year}年${month}月`;
            }
          }
        },
        legend: {
          data: ['登录1天', '登录3天', '登录10天']
        },
        series: [
          {
            name: '登录1天',
            type: 'line',
            data: activeUsers
          },
          {
            name: '登录3天',
            type: 'line',
            data: active3Days
          },
          {
            name: '登录10天',
            type: 'line',
            data: active10Days
          }
        ]
      }
    },
    retentionChartOption() {
      if (!this.activityData?.cohorts?.months) return {};
      
      const { months, new_users, retention_matrix } = this.activityData.cohorts;
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(months);
      
      const filteredMonths = months.slice(startIndex);
      const filteredNewUsers = new_users.slice(startIndex);
      const filteredMatrix = retention_matrix.slice(startIndex);
      
      // 转换数据为热力图格式
      const data = [];
      filteredMatrix.forEach((rates, i) => {
        rates.forEach((rate, j) => {
          data.push([j, i, rate]);  // 交换 i 和 j 以横向显示
        });
      });
      
      return {
        tooltip: {
          position: 'top',
          formatter: function (params) {
            const month = filteredMonths[params.data[1]];
            const monthNum = params.data[0];
            const value = params.data[2];
            return `${month} 新用户<br/>第 ${monthNum} 月留存率: ${value}%<br/>新增用户数: ${filteredNewUsers[params.data[1]]}`;
          }
        },
        grid: {
          top: '50px',
          right: '15%',
          bottom: '15%',
          left: '10%'
        },
        xAxis: {
          type: 'category',
          data: Array.from({ length: filteredMatrix[0].length }, (_, i) => `第${i}月`),
          splitArea: {
            show: true
          }
        },
        yAxis: {
          type: 'category',
          data: filteredMonths,
          splitArea: {
            show: true
          }
        },
        visualMap: {
          min: 0,
          max: 100,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          top: '0',
          text: ['高', '低'],
          inRange: {
            color: ['#fff', '#409EFF']
          }
        },
        series: [{
          name: '留存率',
          type: 'heatmap',
          data: data,
          label: {
            show: true,
            formatter: function(params) {
              return params.data[2] + '%';
            }
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };
    },
    retentionLineOption() {
      if (!this.activityData?.cohorts?.months) return {};
      const { months: cohortMonths, retention_matrix } = this.activityData.cohorts;
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(cohortMonths);
      
      const months = cohortMonths.slice(startIndex);
      const matrix = retention_matrix.slice(startIndex);
      
      // 将每个同期群的留存数据转为一条折线
      const series = months.map((month, index) => ({
        name: month,
        type: 'line',
        data: matrix[index]
      }));

      return {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          type: 'scroll',
          orient: 'vertical',
          right: 10,
          top: 20,
          bottom: 20
        },
        grid: {
          right: '15%'
        },
        xAxis: {
          type: 'category',
          data: Array.from({ length: matrix[0].length }, (_, i) => `第${i}月`)
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '{value}%'
          }
        },
        series
      };
    },
    monthlyNewUsersChartOption() {
      if (!this.activityData?.monthlyNewUsers?.months) return {};
      
      // 直接调用过滤函数
      const startIndex = this.filterDataFrom2024July(this.activityData.monthlyNewUsers.months);
      
      const months = this.activityData.monthlyNewUsers.months.slice(startIndex);
      const newUsers = this.activityData.monthlyNewUsers.newUsers.slice(startIndex);
      
      return {
        ...this.chartBaseOption,
        xAxis: {
          ...this.chartBaseOption.xAxis,
          data: months
        },
        series: [
          {
            name: '月新增用户数',
            type: 'bar',
            data: newUsers,
            itemStyle: {
              color: '#67c23a'
            },
            label: {
              show: true,
              position: 'top'
            }
          }
        ]
      };
    },
    loginDaysDistributionOption() {
      if (!this.loginDaysData?.distribution?.byDayRanges) return {};
      
      // 获取分布数据 - 适配新的数据结构
      const ranges = this.loginDaysData.distribution.byDayRanges.ranges || [];
      const counts = this.loginDaysData.distribution.byDayRanges.counts || [];
      
      // 组合数据
      const data = ranges.map((range, index) => ({
        name: range,
        value: counts[index],
        percentage: ((counts[index] / this.loginDaysData.totalUsers) * 100).toFixed(2)
      }));
      
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}人 ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: ranges
        },
        series: [
          {
            name: '登录天数分布',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 4,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: true,
              formatter: '{b}: {c}人 ({d}%)'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '15',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: true
            },
            data: data
          }
        ]
      };
    }
  },
  created() {
    // 设置当前日期
    const now = new Date();
    this.currentDate = now.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  },
  mounted() {
    this.loadAllData();
    
    // 添加窗口大小调整事件处理，使用节流函数
    window.addEventListener('resize', this.handleWindowResize);
  },
  beforeUnmount() {
    // 移除窗口大小调整事件处理
    window.removeEventListener('resize', this.handleWindowResize);
  },
  methods: {
    // 过滤2024年7月之后的数据
    filterDataFrom2024July(dates) {
      // 防御性检查，避免对undefined调用findIndex
      if (!dates || !Array.isArray(dates)) return 0;
      
      const startIndex = dates.findIndex(date => {
        if (!date) return false;
        // 处理YYYY-MM和YYYY-MM-DD两种格式
        const parts = date.split('-');
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        return (year > 2024) || (year === 2024 && month >= 7);
      });
      return startIndex >= 0 ? startIndex : 0;
    },
    
    // 处理窗口大小调整的节流函数
    handleWindowResize() {
      if (this.resizeObserverThrottleTimer) {
        return;
      }
      
      this.resizeObserverThrottleTimer = setTimeout(() => {
        // 更新图表 key 以强制刷新
        this.chartUpdateKey += 1;
        this.resizeObserverThrottleTimer = null;
      }, 100); // 100ms 的节流
    },
    
    // 整合数据加载方法
    async loadAllData() {
      this.loading = true;
      this.error = null;
      
      try {
        // 使用Promise.all并行请求所有数据
        const [countResponse, activityResponse, newUsersResponse] = await Promise.all([
          axios.get('/api/users/count', this.getAuthHeaders()),
          axios.get('/api/users/activities', this.getAuthHeaders()),
          axios.get('/api/users/new', this.getAuthHeaders())
        ]);
        
        // 处理用户数
        if (countResponse.data.code === 200) {
          this.userCount = countResponse.data.data;
        }
        
        // 处理活动数据
        if (activityResponse.data.code === 200) {
          this.activityData = activityResponse.data.data;
          this.updateTime = this.activityData.updateTime;
          
          // 从activityData中获取登录天数数据
          if (this.activityData.loginDaysStats) {
            this.loginDaysData = this.activityData.loginDaysStats;
          }
        } else if (activityResponse.data.code !== 401 && activityResponse.data.code !== 403) {
          this.error = activityResponse.data.message;
        }
        
        // 处理新用户统计
        if (newUsersResponse.data.code === 200) {
          this.newUsersStats = newUsersResponse.data.data;
        }
      } catch (error) {
        console.error('获取数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.error = `请求失败: ${error.message}`;
        }
      } finally {
        this.loading = false;
      }
    },
    
    // 获取带授权头的配置
    getAuthHeaders() {
      return {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      };
    },
    
    // 刷新活跃度数据
    async refreshActivityData() {
      if (this.refreshing) return;
      
      this.refreshing = true;
      this.refreshError = null;
      
      try {
        const response = await axios.post('/api/users/activities/refresh', {}, this.getAuthHeaders());
        if (response.data.code === 200) {
          // 刷新成功后重新获取数据
          await this.loadAllData();
        } else {
          // 只有非授权错误才设置刷新错误
          if (response.data.code !== 401 && response.data.code !== 403) {
            this.refreshError = response.data.message || '刷新数据失败';
        }
        }
      } catch (error) {
        console.error('刷新活跃度数据失败:', error);
        // 只显示非认证错误
        if (!error.response || (error.response.status !== 401 && error.response.status !== 403)) {
          this.refreshError = `刷新失败: ${error.message}`;
        }
        this.handleAuthError();
      } finally {
        this.refreshing = false;
      }
    },
    
    // 初始化应用
    async initApp() {
      await this.loadAllData();
    },
    
    // 添加格式化账号的方法
    formatAccount(account) {
      // 如果是手机号，返回加密的形式
      if (/^\d{11}$/.test(account)) {
        return account.substring(0, 3) + '****' + account.substring(7);
      }
      return account;
    },
    
    // 处理认证错误
    handleAuthError() {
      this.$emit('auth-error');
    }
  }
}
</script>

<style>
.stats-page {
  padding: 25px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  max-width: 1600px;
  margin: 0 auto;
}

.stats-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.section-header h2 {
  font-size: 16px;
  color: #303133;
  margin: 0;
  padding: 0;
  border: none;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.current-date {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

.realtime-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  padding: 8px;
}

.stat-card {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
  text-align: center;
  border: 1px solid #ebeef5;
}

.stat-title {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.stat-value {
  color: #409EFF;
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 6px;
}

.stat-value.primary {
  color: #303133;
  font-size: 24px;
}

.stat-compare {
  font-size: 12px;
  color: #909399;
}

.refresh-btn {
  padding: 8px 15px;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.refresh-btn:hover {
  background-color: #66b1ff;
}

.refresh-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.update-time {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

.refresh-error {
  padding: 8px 12px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  margin-bottom: 15px;
  border-left: 3px solid #f56c6c;
  text-align: left;
  font-size: 14px;
}

.error {
  padding: 20px;
  background-color: #fef0f0;
  color: #f56c6c;
  border-radius: 4px;
  text-align: center;
}

.stat-group {
  margin-bottom: 25px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  text-align: center;
}

.stat-group:last-child {
  margin-bottom: 0;
}

.stat-group h3 {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
  margin: 0 auto 20px auto;
  padding-bottom: 12px;
  border-bottom: 2px solid #409EFF;
  display: inline-block;
  position: relative;
}

.stat-group h3::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #409EFF;
  box-shadow: 0 1px 3px rgba(64, 158, 255, 0.3);
}

.stat-group h4 {
  color: #606266;
  font-size: 14px;
  margin: 0 0 10px 0;
  font-weight: 500;
  text-align: center;
}

.charts-container, .retention-charts, .login-days-charts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 0 auto;
  max-width: 95%;
}

.chart-section {
  padding: 15px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #ebeef5;
  transition: all 0.3s;
  text-align: center;
}

.chart-section:hover {
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

.chart {
  height: 280px;
}

/* 用户增长统计的三列布局 */
.stat-group:first-child .charts-container {
  grid-template-columns: repeat(3, 1fr);
  margin: 0 auto;
  max-width: 95%;
}

/* 活跃度统计的三列布局 */
.stat-group:nth-child(2) .charts-container,
.stat-group:nth-child(3) .charts-container {
  grid-template-columns: repeat(3, 1fr);
}

/* 留存统计的横向布局样式 */
.retention-charts, .login-days-charts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 0 auto;
  max-width: 95%;
}

/* 调整留存图表的高度 */
.retention-charts .chart {
  height: 400px;
}

/* 响应式布局调整 */
@media (max-width: 1600px) {
  .stat-group:first-child .charts-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .charts-container,
  .stat-group:nth-child(2) .charts-container,
  .stats-section:last-child .charts-container {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .chart {
    height: 260px;
  }
  
  .stat-group:last-child .chart {
    height: 300px;
  }
}

@media (max-width: 1200px) {
  .realtime-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .stats-page {
    max-width: 1200px;
  }
  
  .chart {
    height: 280px;
  }
  
  .retention-charts {
    grid-template-columns: 1fr;
  }
  
  .retention-charts .chart {
    height: 350px;
  }
}

@media (max-width: 960px) {
  .charts-container,
  .stat-group:first-child .charts-container,
  .stat-group:nth-child(2) .charts-container {
    grid-template-columns: 1fr;
  }
  
  .chart {
    height: 240px;
  }
  
  .stat-group:last-child .chart {
    height: 280px;
  }
  
  .stats-page {
    max-width: 100%;
    padding: 15px;
  }
  
  .stat-group {
    padding: 10px;
  }
}

@media (max-width: 768px) {
  .realtime-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .stat-card {
    padding: 12px;
  }
  
  .stat-value {
    font-size: 20px;
  }
  
  .stat-value.primary {
    font-size: 22px;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    margin-top: 10px;
  }
}

@media (max-width: 480px) {
  .realtime-stats {
    grid-template-columns: 1fr;
  }
  
  .stats-page {
    padding: 10px;
  }
  
  .stat-group {
    padding: 8px;
  }
  
  .chart {
    height: 220px;
  }
  
  .retention-charts .chart {
    height: 300px;
  }
}

/* 留存率热力图占据整行 */
.stat-group:last-child .chart-section {
  grid-column: auto;
}

/* 留存率热力图高度 */
.stat-group:last-child .chart {
  height: 320px;
}

/* 用户登录统计布局 */
.login-days-charts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

/* 登录统计摘要样式 */
.login-stats-summary {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  border: 1px solid #ebeef5;
}

.login-stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.login-stat-label {
  font-size: 14px;
  color: #606266;
}

.login-stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #409EFF;
}

@media (max-width: 768px) {
  .login-stats-summary {
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 8px;
  }
}
</style> 